import React, { useEffect, useState } from "react";

import ChatPromptInput from "src/reusable/Chat/PromptInput/ChatPromptInput";
import { NucToast } from "src/reusable/components/Toast";
import WorkflowChatInputButtons from "./WorkflowChatInputButtons";

type WorkflowSplashChatInputProps = {
  /**
   * Prefill for the input. Re-applied whenever it changes UNTIL the user
   * first types, so selecting inputs on the splash updates the prompt live
   * without ever clobbering the user's own edits.
   */
  defaultValue: string;
  /** Runs the product's kickoff; resolves true when navigation is underway. */
  sendUserMessage: (text: string) => Promise<boolean>;
  isChatLoading: boolean;
  placeholder?: string;
  /** Backend product slug (ChatPromptInput's upload paths key on it). */
  product: string;
};

/**
 * The splash-page chat box for workflow products. Sending does NOT open a
 * stream — the caller's `sendUserMessage` runs the kickoff pipeline (create
 * dialogue/run, stash the text as the initial message, navigate) and the
 * chat page fires the actual turn on mount. File attachments are disabled:
 * ChatPromptInput's attach path creates its own dialogue outside the
 * kickoff, which would orphan it from the product's run state; uploads go
 * through the product's input card instead.
 */
const WorkflowSplashChatInput = ({
  defaultValue,
  sendUserMessage,
  isChatLoading,
  placeholder,
  product,
}: WorkflowSplashChatInputProps) => {
  const [text, setText] = useState<string>(defaultValue);
  const [userEdited, setUserEdited] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!userEdited) {
      setText(defaultValue);
    }
  }, [defaultValue, userEdited]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserEdited(true);
    setText(event.target.value);
  };

  const handleSubmit = async () => {
    if (isSubmitting || isChatLoading) {
      return;
    }
    if (text.trim() === "") {
      NucToast({
        message: "Please enter a message",
        type: "error",
        hideProgressBar: false,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const sent = await sendUserMessage(text);
      if (sent) {
        setText("");
        setUserEdited(false);
      }
    } catch (error) {
      console.error("[WorkflowSplashChatInput] Kickoff failed:", error);
      NucToast({
        message: "Failed to start. Please try again.",
        type: "error",
        hideProgressBar: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ChatPromptInput
      onSubmit={handleSubmit}
      isInputting={text !== ""}
      isChatLoading={isChatLoading || isSubmitting}
      isGenerating={false}
      onStopChat={() => {}}
      value={text}
      onChange={handleChange}
      InputButtons={WorkflowChatInputButtons}
      isSplashScreen={true}
      placeholder={placeholder}
      product={product}
      noFiles={true}
    />
  );
};

export default WorkflowSplashChatInput;
